import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, MoreVertical, Building2, Phone, Mail, MapPin, 
  User, Pencil, Trash2, Bell, Send, Paperclip, X, Loader2
} from 'lucide-react';

// --- DIALOGUE DE RELANCE (Avec Pièces Jointes) ---
const FollowUpDialog = ({ client, open, onOpenChange }) => {
  const { token } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedQuote, setSelectedQuote] = useState('none');
  const [files, setFiles] = useState([]); // Gestion des fichiers
  const [isLoading, setIsLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);

  // Reset state on open
  React.useEffect(() => {
    if (open && client) {
      setFiles([]);
      setSelectedQuote('none');
      // Fetch quotes
      fetch(`${API_URL}/quotes`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
          const clientQuotes = data.filter(q => q.client_id === client.id);
          setQuotes(clientQuotes);
        })
        .catch(() => setQuotes([]));

      // Fetch template
      fetch(`${API_URL}/clients/${client.id}/follow-up-template`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
          setSubject(data.subject);
          setBody(data.body);
        })
        .catch(console.error);
    }
  }, [open, client, token]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles([...files, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!subject || !body) {
      toast.error('Sujet et message requis');
      return;
    }

    setIsLoading(true);
    try {
      // Utilisation de FormData pour l'upload de fichiers
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      if (selectedQuote !== 'none') {
        formData.append('quote_id', selectedQuote);
      }
      
      // Ajout des fichiers
      files.forEach((file) => {
        formData.append('files', file);
      });

      const res = await fetch(`${API_URL}/clients/${client.id}/follow-up`, {
        method: 'POST',
        headers: {
          // Ne PAS mettre 'Content-Type': 'application/json' ici, fetch le gère pour FormData
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur lors de l\'envoi');
      }

      toast.success('Email envoyé avec succès');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" /> Relancer {client.name}
          </DialogTitle>
          <DialogDescription>Envoyez un email avec pièces jointes.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/50 p-3 rounded-lg text-sm flex justify-between">
            <span>À : <strong>{client.email || 'Pas d\'email'}</strong></span>
          </div>

          <div className="grid grid-cols-2 gap-4">
             <div className="space-y-2">
               <Label>Lier un Devis (Génèrera le PDF)</Label>
               <Select value={selectedQuote} onValueChange={setSelectedQuote}>
                 <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                 <SelectContent>
                   <SelectItem value="none">Aucun</SelectItem>
                   {quotes.map((q) => (
                     <SelectItem key={q.id} value={q.id}>{q.reference} - {formatCurrency(q.total_ttc)}</SelectItem>
                   ))}
                 </SelectContent>
               </Select>
             </div>
             
             {/* Zone Upload Fichiers */}
             <div className="space-y-2">
                <Label>Pièces jointes supplémentaires</Label>
                <div className="flex items-center gap-2">
                    <Input 
                        id="file-upload" 
                        type="file" 
                        multiple 
                        className="hidden" 
                        onChange={handleFileChange} 
                    />
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => document.getElementById('file-upload').click()}>
                        <Paperclip className="w-4 h-4 mr-2" /> Ajouter fichiers
                    </Button>
                </div>
             </div>
          </div>

          {/* Liste des fichiers sélectionnés */}
          {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                      <Badge key={i} variant="secondary" className="flex items-center gap-1 pl-2 pr-1 py-1">
                          <span className="truncate max-w-[150px]">{f.name}</span>
                          <Button variant="ghost" size="icon" className="h-4 w-4 rounded-full hover:bg-destructive/20 hover:text-destructive" onClick={() => removeFile(i)}>
                              <X className="w-3 h-3" />
                          </Button>
                      </Badge>
                  ))}
              </div>
          )}

          <div className="space-y-2">
            <Label>Sujet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet..." />
          </div>

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Votre message..." />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={handleSend} disabled={isLoading || !client.email}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- DIALOGUE CLIENT (CRUD) ---
const ClientFormDialog = ({ client, open, onOpenChange, onSave }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', address: '', notes: '' });

  React.useEffect(() => {
    if (open) {
        setForm(client ? { ...client, notes: client.notes || '' } : { name: '', company: '', email: '', phone: '', address: '', notes: '' });
    }
  }, [client, open]);

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{client ? 'Modifier' : 'Nouveau'} client</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2"><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="space-y-2"><Label>Entreprise</Label><Input value={form.company || ''} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Téléphone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="space-y-2"><Label>Adresse</Label><Textarea value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} rows={2} /></div>
          <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit">{client ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- PAGE PRINCIPALE ---
const ClientsPage = () => {
  const { token, can } = useAuthStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [followUpClient, setFollowUpClient] = useState(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const canEditClients = can('can_edit_clients');

  const { data: clients = [], isLoading } = useQuery({
  queryKey: ['clients'],
  queryFn: async () => {
    const res = await fetch(`${API_URL}/clients`, { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    return res.json();
  },
  // On force le rafraîchissement à chaque fois que la fenêtre reprend le focus
  refetchOnWindowFocus: true, 
});

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed'); return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); toast.success('Client créé'); setIsFormOpen(false); },
    onError: () => toast.error('Erreur création'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/clients/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed'); return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); toast.success('Client mis à jour'); setIsFormOpen(false); },
    onError: () => toast.error('Erreur mise à jour'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/clients/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['clients'] }); toast.success('Client supprimé'); },
  });

  const handleSave = (data) => {
    if (editingClient) updateMutation.mutate({ id: editingClient.id, data });
    else createMutation.mutate(data);
  };

  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    (c.company && c.company.toLowerCase().includes(search.toLowerCase())) ||
    (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Clients</h1>
          <p className="text-muted-foreground">Gérez votre base de contacts</p>
        </div>
        <Button onClick={() => { setEditingClient(null); setIsFormOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Nouveau Client
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Rechercher..." className="pl-9 max-w-md" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {isLoading ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4"><Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div> : 
       filteredClients.length === 0 ? <div className="text-center py-12 text-muted-foreground">Aucun client trouvé</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow group relative">
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setEditingClient(client); setIsFormOpen(true); }}><Pencil className="w-4 h-4 mr-2" /> Modifier</DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => { if(window.confirm('Supprimer ?')) deleteMutation.mutate(client.id); }}><Trash2 className="w-4 h-4 mr-2" /> Supprimer</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold">{client.name.charAt(0).toUpperCase()}</div>
                    <div>
                        <CardTitle className="text-base font-bold">{client.name}</CardTitle>
                        {client.company && <p className="text-sm text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" /> {client.company}</p>}
                    </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {client.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /> <a href={`mailto:${client.email}`} className="hover:text-primary">{client.email}</a></div>}
                {client.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> <a href={`tel:${client.phone}`} className="hover:text-primary">{client.phone}</a></div>}
                {client.address && <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="w-3.5 h-3.5 mt-0.5" /> {client.address}</div>}
                
                {/* --- NOUVEAUTÉ : DATE DE RELANCE --- */}
                {client.last_followup_date && (
                    <div className="pt-2">
                        <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-200 bg-orange-50">
                            Relancé le {formatDate(client.last_followup_date)}
                        </Badge>
                    </div>
                )}

                <div className="pt-2">
                    <Button variant="secondary" size="sm" className="w-full" onClick={() => { setFollowUpClient(client); setFollowUpOpen(true); }}>
                        <Bell className="w-3.5 h-3.5 mr-2" /> Relancer par mail
                    </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ClientFormDialog client={editingClient} open={isFormOpen} onOpenChange={(o) => { setIsFormOpen(o); if(!o) setEditingClient(null); }} onSave={handleSave} />
      
      {/* Dialogue de relance (inclus dans la page) */}
      <FollowUpDialog client={followUpClient} open={followUpOpen} onOpenChange={(o) => { setFollowUpOpen(o); if(!o) setFollowUpClient(null); }} />
    </div>
  );
};

export default ClientsPage;